import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  isAccessRole,
  isJoinSource,
  isMembershipStatus,
  isPracticeKind,
  isProfessionalRole,
  type AdminPracticeDetail,
  type AdminPracticeLink,
  type AdminPracticeListItem,
  type AdminPracticeMember,
  type AdminPracticeOffer,
  type MyPractice,
  type PracticeCreateInput,
  type PracticeDetail,
  type PracticeInviteCandidate,
  type PracticeMember,
  type PracticeMembership,
  type PracticeSearchHit,
  type PracticeServiceRequest,
  type PracticeUpdateInput,
  type ProfilePractice,
  type PublicProfile,
} from "@/lib/practices/types";

type MyPracticeRow = {
  id: string;
  name: string;
  kind: string;
  city: string;
  address_line: string | null;
  postal_code: string | null;
  is_active: boolean;
  member_count: number;
  professional_role: string;
  access_role: string;
  membership_status: string;
  is_primary: boolean;
  join_source: string;
  invited_by_name: string | null;
  created_at: string;
};

type SearchRow = {
  id: string;
  name: string;
  kind: string;
  city: string;
  member_count: number;
  membership_status: string | null;
};

type MemberRow = {
  id: string;
  user_id: string;
  full_name: string;
  professional_title: string | null;
  avatar_path: string | null;
  professional_role: string;
  access_role: string;
  status: string;
  is_primary: boolean;
  join_source: string;
  invited_by: string | null;
  invited_by_name: string | null;
  joined_at: string | null;
  created_at: string;
};

type ServiceRequestRow = {
  id: string;
  status: string;
  created_at: string;
  updated_at: string;
  service_name: string;
  service_slug: string;
  user_id: string;
  user_name: string;
  offer_status: string | null;
  offer_summary: string | null;
};

type ProfilePracticeRow = {
  id: string;
  name: string;
  kind: string;
  city: string;
  is_primary: boolean;
  can_open: boolean;
};

type AdminListRow = {
  id: string;
  name: string;
  kind: string;
  city: string;
  is_active: boolean;
  archived_at: string | null;
  member_count: number;
  created_at: string;
  active_request_count: number;
};

function mapMembership(value: Record<string, unknown> | null): PracticeMembership | null {
  if (!value) {
    return null;
  }

  const professionalRole = String(value.professional_role ?? "");
  const accessRole = String(value.access_role ?? "");
  const status = String(value.status ?? "");
  const joinSource = String(value.join_source ?? "");

  if (
    !isProfessionalRole(professionalRole) ||
    !isAccessRole(accessRole) ||
    !isMembershipStatus(status) ||
    !isJoinSource(joinSource)
  ) {
    return null;
  }

  return {
    professionalRole,
    accessRole,
    status,
    isPrimary: Boolean(value.is_primary),
    joinSource,
  };
}

function mapMyPractice(row: MyPracticeRow): MyPractice | null {
  if (
    !isPracticeKind(row.kind) ||
    !isProfessionalRole(row.professional_role) ||
    !isAccessRole(row.access_role) ||
    !isMembershipStatus(row.membership_status) ||
    !isJoinSource(row.join_source)
  ) {
    return null;
  }

  return {
    id: row.id,
    name: row.name,
    kind: row.kind,
    city: row.city,
    addressLine: row.address_line,
    postalCode: row.postal_code,
    isActive: row.is_active,
    memberCount: row.member_count,
    professionalRole: row.professional_role,
    accessRole: row.access_role,
    membershipStatus: row.membership_status,
    isPrimary: row.is_primary,
    joinSource: row.join_source,
    invitedByName: row.invited_by_name,
    createdAt: row.created_at,
  };
}

function mapDetail(value: Record<string, unknown>): PracticeDetail | null {
  const kind = String(value.kind ?? "");
  if (!isPracticeKind(kind) || typeof value.id !== "string") {
    return null;
  }

  const membershipValue =
    value.membership && typeof value.membership === "object"
      ? (value.membership as Record<string, unknown>)
      : null;

  return {
    id: value.id,
    name: String(value.name ?? ""),
    kind,
    city: String(value.city ?? ""),
    memberCount: Number(value.member_count ?? 0),
    isActive: Boolean(value.is_active),
    archivedAt: typeof value.archived_at === "string" ? value.archived_at : null,
    createdAt: typeof value.created_at === "string" ? value.created_at : null,
    canManage: Boolean(value.can_manage),
    canArchive: Boolean(value.can_archive),
    isMember: Boolean(value.is_member),
    publicOnly: Boolean(value.public_only),
    membership: mapMembership(membershipValue),
    postalCode: typeof value.postal_code === "string" ? value.postal_code : null,
    addressLine: typeof value.address_line === "string" ? value.address_line : null,
    phone: typeof value.phone === "string" ? value.phone : null,
    email: typeof value.email === "string" ? value.email : null,
    summary: typeof value.summary === "string" ? value.summary : null,
    externalIdentifier:
      typeof value.external_identifier === "string"
        ? value.external_identifier
        : null,
    contactUserId:
      typeof value.contact_user_id === "string" ? value.contact_user_id : null,
  };
}

export async function listMyPractices(): Promise<MyPractice[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_my_practices");

  if (error) {
    throw error;
  }

  return ((data ?? []) as MyPracticeRow[]).flatMap((row) => {
    const mapped = mapMyPractice(row);
    return mapped ? [mapped] : [];
  });
}

export async function searchPractices(query: string): Promise<PracticeSearchHit[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("search_practices", {
    search_query: query,
  });

  if (error) {
    throw error;
  }

  return ((data ?? []) as SearchRow[]).flatMap((row) => {
    if (!isPracticeKind(row.kind)) {
      return [];
    }

    return [
      {
        id: row.id,
        name: row.name,
        kind: row.kind,
        city: row.city,
        memberCount: row.member_count,
        membershipStatus:
          row.membership_status && isMembershipStatus(row.membership_status)
            ? row.membership_status
            : null,
      },
    ];
  });
}

export async function getPracticeDetail(
  practiceId: string,
): Promise<PracticeDetail | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_practice_detail", {
    target_practice_id: practiceId,
  });

  if (error) {
    throw error;
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  return mapDetail(data as Record<string, unknown>);
}

export async function createPractice(input: PracticeCreateInput): Promise<string> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_practice", {
    practice_name: input.name.trim(),
    practice_kind: input.kind,
    practice_city: input.city.trim(),
    practice_postal_code: input.postalCode?.trim() || null,
    practice_address_line: input.addressLine?.trim() || null,
    practice_phone: input.phone?.trim() || null,
    practice_email: input.email?.trim() || null,
    practice_identifier: input.identifier?.trim() || null,
    practice_summary: input.summary?.trim() || null,
  });

  if (error) {
    throw error;
  }

  return String(data);
}

export async function setPracticeArchived(
  practiceId: string,
  archived: boolean,
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("set_practice_archived", {
    target_practice_id: practiceId,
    next_archived: archived,
  });

  if (error) {
    throw error;
  }
}

export async function updatePractice(
  practiceId: string,
  input: PracticeUpdateInput,
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("update_practice", {
    target_practice_id: practiceId,
    practice_name: input.name.trim(),
    practice_kind: input.kind,
    practice_city: input.city.trim(),
    practice_postal_code: input.postalCode?.trim() || null,
    practice_address_line: input.addressLine?.trim() || null,
    practice_phone: input.phone?.trim() || null,
    practice_email: input.email?.trim() || null,
    practice_identifier: input.identifier?.trim() || null,
    practice_summary: input.summary?.trim() || null,
  });

  if (error) {
    throw error;
  }
}

export async function listPracticeMembers(
  practiceId: string,
): Promise<PracticeMember[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_practice_members", {
    target_practice_id: practiceId,
  });

  if (error) {
    throw error;
  }

  return ((data ?? []) as MemberRow[]).flatMap((row) => {
    if (
      !isProfessionalRole(row.professional_role) ||
      !isAccessRole(row.access_role) ||
      !isMembershipStatus(row.status) ||
      !isJoinSource(row.join_source)
    ) {
      return [];
    }

    return [
      {
        id: row.id,
        userId: row.user_id,
        fullName: row.full_name,
        professionalTitle: row.professional_title,
        avatarPath: row.avatar_path,
        professionalRole: row.professional_role,
        accessRole: row.access_role,
        status: row.status,
        isPrimary: row.is_primary,
        joinSource: row.join_source,
        invitedBy: row.invited_by,
        invitedByName: row.invited_by_name,
        joinedAt: row.joined_at,
        createdAt: row.created_at,
      },
    ];
  });
}

export async function searchPracticeInviteCandidates(
  practiceId: string,
  query: string,
): Promise<PracticeInviteCandidate[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc(
    "search_practice_invite_candidates",
    {
      target_practice_id: practiceId,
      search_query: query,
    },
  );

  if (error) {
    throw error;
  }

  return ((data ?? []) as {
    id: string;
    full_name: string;
    professional_title: string | null;
    avatar_path: string | null;
  }[]).map((row) => ({
    id: row.id,
    fullName: row.full_name,
    professionalTitle: row.professional_title,
    avatarPath: row.avatar_path,
  }));
}

export async function invitePracticeMember(
  practiceId: string,
  userId: string,
  professionalRole: PracticeMember["professionalRole"],
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("invite_practice_member", {
    target_practice_id: practiceId,
    target_user_id: userId,
    target_professional_role: professionalRole,
  });

  if (error) {
    throw error;
  }
}

export async function respondPracticeInvite(
  practiceId: string,
  accept: boolean,
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("respond_practice_invite", {
    target_practice_id: practiceId,
    accept,
  });

  if (error) {
    throw error;
  }
}

export async function requestPracticeJoin(
  practiceId: string,
  professionalRole: PracticeMember["professionalRole"],
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("request_practice_join", {
    target_practice_id: practiceId,
    target_professional_role: professionalRole,
  });

  if (error) {
    throw error;
  }
}

export async function reviewPracticeJoin(
  practiceId: string,
  userId: string,
  approve: boolean,
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("review_practice_join", {
    target_practice_id: practiceId,
    target_user_id: userId,
    approve,
  });

  if (error) {
    throw error;
  }
}

export async function updatePracticeMember(
  practiceId: string,
  userId: string,
  professionalRole: PracticeMember["professionalRole"],
  accessRole: PracticeMember["accessRole"],
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("update_practice_member", {
    target_practice_id: practiceId,
    target_user_id: userId,
    target_professional_role: professionalRole,
    target_access_role: accessRole,
  });

  if (error) {
    throw error;
  }
}

export async function removePracticeMember(
  practiceId: string,
  userId: string,
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("remove_practice_member", {
    target_practice_id: practiceId,
    target_user_id: userId,
  });

  if (error) {
    throw error;
  }
}

export async function setPrimaryPractice(practiceId: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("set_primary_practice", {
    target_practice_id: practiceId,
  });

  if (error) {
    throw error;
  }
}

export async function listPracticeServiceRequests(
  practiceId: string,
): Promise<PracticeServiceRequest[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_practice_service_requests", {
    target_practice_id: practiceId,
  });

  if (error) {
    throw error;
  }

  return ((data ?? []) as ServiceRequestRow[]).map((row) => ({
    id: row.id,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    serviceName: row.service_name,
    serviceSlug: row.service_slug,
    userId: row.user_id,
    userName: row.user_name,
    offerStatus: row.offer_status,
    offerSummary: row.offer_summary,
  }));
}

export async function listProfilePractices(
  userId: string,
): Promise<ProfilePractice[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_profile_practices", {
    target_user_id: userId,
  });

  if (error) {
    throw error;
  }

  return ((data ?? []) as ProfilePracticeRow[]).flatMap((row) => {
    if (!isPracticeKind(row.kind)) {
      return [];
    }

    return [
      {
        id: row.id,
        name: row.name,
        kind: row.kind,
        city: row.city,
        isPrimary: row.is_primary,
        canOpen: row.can_open,
      },
    ];
  });
}

export async function getPublicProfile(
  userId: string,
): Promise<PublicProfile | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_public_profile", {
    target_user_id: userId,
  });

  if (error) {
    throw error;
  }

  const profile = data?.[0];

  if (!profile) {
    return null;
  }

  return {
    id: profile.id,
    fullName: profile.full_name,
    professionalTitle: profile.professional_title,
    occupation: profile.occupation,
    city: profile.city,
    bio: profile.bio,
    avatarPath: profile.avatar_path ?? null,
  };
}

export async function listAdminPractices(): Promise<AdminPracticeListItem[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_practices");

  if (error) {
    throw error;
  }

  return ((data ?? []) as AdminListRow[]).flatMap((row) => {
    if (!isPracticeKind(row.kind)) {
      return [];
    }

    return [
      {
        id: row.id,
        name: row.name,
        kind: row.kind,
        city: row.city,
        isActive: row.is_active,
        archivedAt: row.archived_at,
        memberCount: row.member_count,
        createdAt: row.created_at,
        activeRequestCount: row.active_request_count,
      },
    ];
  });
}

function mapAdminLink(value: Record<string, unknown>): AdminPracticeLink | null {
  if (typeof value.id !== "string") {
    return null;
  }

  return {
    id: value.id,
    status: String(value.status ?? ""),
    createdAt: String(value.created_at ?? ""),
    serviceName: String(value.service_name ?? ""),
    userName: String(value.user_name ?? ""),
  };
}

function mapAdminMember(value: Record<string, unknown>): AdminPracticeMember | null {
  const professionalRole = String(value.professional_role ?? "");
  const accessRole = String(value.access_role ?? "");
  const status = String(value.status ?? "");

  if (
    typeof value.user_id !== "string" ||
    !isProfessionalRole(professionalRole) ||
    !isAccessRole(accessRole) ||
    !isMembershipStatus(status)
  ) {
    return null;
  }

  return {
    userId: value.user_id,
    fullName: String(value.full_name ?? ""),
    professionalRole,
    accessRole,
    status,
    isPrimary: Boolean(value.is_primary),
  };
}

export async function getAdminPracticeDetail(
  practiceId: string,
): Promise<AdminPracticeDetail | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_practice_detail", {
    target_practice_id: practiceId,
  });

  if (error) {
    throw error;
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  const value = data as Record<string, unknown>;
  const kind = String(value.kind ?? "");
  if (!isPracticeKind(kind) || typeof value.id !== "string") {
    return null;
  }

  const members = Array.isArray(value.members)
    ? value.members.flatMap((item) => {
        if (!item || typeof item !== "object") {
          return [];
        }
        const mapped = mapAdminMember(item as Record<string, unknown>);
        return mapped ? [mapped] : [];
      })
    : [];

  const serviceRequests = Array.isArray(value.service_requests)
    ? value.service_requests.flatMap((item) => {
        if (!item || typeof item !== "object") {
          return [];
        }
        const mapped = mapAdminLink(item as Record<string, unknown>);
        return mapped ? [mapped] : [];
      })
    : [];

  const leads = Array.isArray(value.leads)
    ? value.leads.flatMap((item) => {
        if (!item || typeof item !== "object") {
          return [];
        }
        const mapped = mapAdminLink(item as Record<string, unknown>);
        return mapped ? [mapped] : [];
      })
    : [];

  const offers: AdminPracticeOffer[] = Array.isArray(value.offers)
    ? value.offers.flatMap((item) => {
        if (!item || typeof item !== "object") {
          return [];
        }
        const row = item as Record<string, unknown>;
        if (typeof row.id !== "string") {
          return [];
        }
        return [
          {
            id: row.id,
            status: String(row.status ?? ""),
            summary: String(row.summary ?? ""),
            amount:
              typeof row.amount === "number"
                ? row.amount
                : row.amount
                  ? Number(row.amount)
                  : null,
            currency: String(row.currency ?? "HUF"),
            createdAt: String(row.created_at ?? ""),
            leadId: String(row.lead_id ?? ""),
          },
        ];
      })
    : [];

  return {
    id: value.id,
    name: String(value.name ?? ""),
    kind,
    city: String(value.city ?? ""),
    postalCode: typeof value.postal_code === "string" ? value.postal_code : null,
    addressLine:
      typeof value.address_line === "string" ? value.address_line : null,
    phone: typeof value.phone === "string" ? value.phone : null,
    email: typeof value.email === "string" ? value.email : null,
    summary: typeof value.summary === "string" ? value.summary : null,
    externalIdentifier:
      typeof value.external_identifier === "string"
        ? value.external_identifier
        : null,
    isActive: Boolean(value.is_active),
    archivedAt: typeof value.archived_at === "string" ? value.archived_at : null,
    createdAt: String(value.created_at ?? ""),
    updatedAt: String(value.updated_at ?? ""),
    memberCount: Number(value.member_count ?? 0),
    members,
    serviceRequests,
    leads,
    offers,
  };
}
